import React, { useState } from 'react'
import Input from '../../components/elements/inputs/Input'
import { useTranslation } from 'next-i18next'
import { GetServerSideProps, GetServerSidePropsContext } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import Link from 'next/link'
import Button from '../../components/elements/Button'
import { routes } from '../../utils/routes'
import { Controller, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import AttentionBox from '../../components/elements/AttentionBox'
import AuthLayout from '../../layouts/AuthLayout'
import { fetcher } from '../../api/io'
import { Api } from '../../../types/api'
import OnboardingImage from '../../components/elements/OnboardingImage'

type IFormData = {
    email: string
}

export const getServerSideProps: GetServerSideProps = async (ctx: GetServerSidePropsContext) => {
    return {
        props: {
            ...(await serverSideTranslations(ctx.locale ? ctx.locale : '', ['common', 'trouble-signing-in'])),
        },
    }
}

const Trouble = () => {
    const { t } = useTranslation(['common', 'trouble-signing-in'])

    const [requested, setRequested] = useState<boolean>(false)
    const {
        handleSubmit,
        control,
        formState: { errors, isSubmitting },
    } = useForm<IFormData>({
        mode: 'onTouched',
    })

    const onSubmit = async (data: IFormData) => {
        if (isSubmitting) return

        const id = toast.loading(<>{t('trouble-toast', { ns: 'trouble-signing-in' })}</>, { toastId: 'trouble-toast' })
        try {
            await fetcher<Api.SendResetPasswordRequest, Api.SendResetPasswordResponse | Api.ConstraintViolationList>({
                url: '/account/request_reset_password',
                params: {
                    email: data.email,
                },
            })

            setRequested(true)
            toast.update(id, {
                render: <>{t('trouble-success-toast', { ns: 'trouble-signing-in' })}</>,
                type: 'success',
                isLoading: false,
                autoClose: 3000,
                closeOnClick: true,
                closeButton: true,
                toastId: 'register-toast',
            })
        } catch (e) {
            console.log(e)
            toast.update(id, {
                render: <>{t('trouble-problem-toast', { ns: 'trouble-signing-in' })}</>,
                type: 'error',
                isLoading: false,
                autoClose: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                closeButton: true,
                toastId: 'trouble-toast',
            })
        }
    }

    return (
        <AuthLayout>
            <main className="min-h-screen lg:overflow-hidden">
                <div className="flex min-h-screen">
                    <div className="relative hidden w-0 flex-1 lg:block min-h-full">
                        <OnboardingImage />
                    </div>
                    <div className="flex flex-1 flex-col justify-center py-12 px-4 sm:px-6 lg:flex-none lg:px-24 xl:px-52  mt-[var(--dann-navbar-height-lg)]">
                        <div className="mx-auto w-full max-w-sm lg:w-96">
                            <div>
                                <h2>{t('title', { ns: 'trouble-signing-in' })}</h2>
                                <p className="mt-4">{t('description', { ns: 'trouble-signing-in' })}</p>
                            </div>

                            <div className="mt-8">
                                {requested ? (
                                    <AttentionBox
                                        variant="success"
                                        icon="ThumbUpIcon"
                                        title={t('request-success-title', { ns: 'trouble-signing-in' })}
                                        text={t('request-success-description', { ns: 'trouble-signing-in' })}
                                    />
                                ) : (
                                    <form className="space-y-6" onSubmit={handleSubmit(onSubmit)} noValidate={true}>
                                        <div className="relative">
                                            <Controller
                                                name="email"
                                                rules={{
                                                    required: t('email-required', { ns: 'trouble-signing-in' }),
                                                    pattern: {
                                                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                        message: t('email-invalid', { ns: 'trouble-signing-in' }),
                                                    },
                                                }}
                                                control={control}
                                                defaultValue={''}
                                                render={({ field }) => (
                                                    <Input
                                                        {...field}
                                                        label={t('email')}
                                                        type="email"
                                                        placeholder={t('email-placeholder')}
                                                        required
                                                    />
                                                )}
                                            />
                                            {errors.email && (
                                                <p
                                                    className="absolute text-xs text-red-500 -bottom-5 inset-x-0 text-right"
                                                    role="alert"
                                                >
                                                    {errors.email?.message}
                                                </p>
                                            )}
                                        </div>

                                        <div>
                                            <Button variant="primary" type="submit" className="w-full  mt-4">
                                                {t('send')}
                                            </Button>
                                        </div>
                                    </form>
                                )}
                                <div className="flex items-center justify-between mt-6">
                                    <div className="text-sm">
                                        <Link href={routes.signIn.path}>
                                            <a>{t('back-to-sign-in')}</a>
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </AuthLayout>
    )
}

export default Trouble
